import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getConsent, setConsent, subscribeToConsent } from "@/lib/consent";
import { analyticsEnabled } from "@/lib/analytics";
import { clarityEnabled } from "@/lib/clarity";
import "./cookie-consent.css";

/**
 * Cookie consent banner.
 *
 * Only shown when there is actually something to consent to: with no GA measurement ID
 * configured the app sets nothing but strictly necessary storage, and a banner asking about
 * cookies that do not exist is just noise.
 */
export function CookieConsent() {
  const [decision, setDecision] = useState(() => getConsent());
  const [closing, setClosing] = useState(false);

  useEffect(
    () =>
      subscribeToConsent((next) => {
        setDecision(next);
        /* Clear the exit state whenever the choice is withdrawn ("Manage cookies"). Without
           this the flag stays true from the previous dismissal, so the banner re-mounted
           already carrying .is-closing and played its fade-out immediately — it appeared for
           a fraction of a second and vanished. */
        if (next === null) setClosing(false);
      }),
    [],
  );

  if ((!analyticsEnabled && !clarityEnabled) || decision !== null) return null;

  const choose = (next: "accepted" | "rejected") => {
    // Let the exit transition play before the element leaves the tree.
    setClosing(true);
    window.setTimeout(() => setConsent(next), 180);
  };

  return (
    <div
      className={`cc-root${closing ? " is-closing" : ""}`}
      role="dialog"
      aria-live="polite"
      aria-label="Cookie preferences"
    >
      <div className="cc-panel">
        <div className="cc-copy">
          <p className="cc-title">We value your privacy</p>
          <p className="cc-body">
            We use cookies to keep BunnyCal working, and measure how the site is used without
            cookies. With your permission we also use analytics cookies to understand it better.
            You can accept or decline analytics cookies, and change your choice at any time. Read
            our{" "}
            <Link className="cc-link" to="/privacy">
              Privacy Policy
            </Link>
            .
          </p>
        </div>
        <div className="cc-actions">
          {/* Both options are given equal prominence — a "reject" that is harder to find than
              "accept" is not freely given consent under GDPR. */}
          <button type="button" className="cc-btn cc-btn-ghost" onClick={() => choose("rejected")}>
            Reject all
          </button>
          <button type="button" className="cc-btn cc-btn-primary" onClick={() => choose("accepted")}>
            Accept all
          </button>
        </div>
      </div>
    </div>
  );
}
